import { useEffect, useMemo, useRef, useState, type FormEvent, type MutableRefObject } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, CheckCircle2, Loader2, Sparkles, Wand2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe, type StripeElementLocale } from '@stripe/stripe-js';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { CountrySelect } from '@/components/ui/country-select';
import { LanguageSwitcher } from '@/components/language-switcher';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { usePublicProviderTermsUrl } from '@/hooks/provider/useProviderTermsSettings';
import { usePublicProviderCheckoutContent } from '@/hooks/provider/useProviderCheckoutContent';
import { usePublicCheckout, useSubmitPublicCheckout } from '@/hooks/public/usePublicCheckout';
import type {
  PublicCheckoutFieldKey,
  PublicCheckoutFieldSetting,
  PublicCreditPackage,
  PublicPlan,
  PublicPlanPricing,
} from '@/types/public-checkout';
import { resolveAuthOriginFromCheckout } from '@/lib/checkout-origin';
import { redirectToSuccessPage } from '@/lib/success-redirect';
import { cn } from '@/lib/utils';
import { normalizeEmail } from '@/lib/email';
import { useCreateProviderCustomer, providerCustomerKeys } from '@/hooks/provider/useProviderCustomers';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ProviderCustomer } from '@/types/provider-customers';
import type { CustomerTeam } from '@/mocks/data/customers/types';
import { customersService } from '@/services/customers.service';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { normalizeLanguageCode } from '@/lib/i18n/normalize-language';
import { useAuthPageTheme } from '@/hooks/useAuthPageTheme';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { PublicCheckoutRequestError } from '@/services/public-checkout.service';
import { buildPrefillValues as buildPrefillValuesUtil } from '@/lib/checkout/prefill';

type CheckoutFieldConfig = {
  key: PublicCheckoutFieldKey;
  formKey:
    | 'company'
    | 'phone'
    | 'billingAddressLine1'
    | 'billingAddressLine2'
    | 'billingAddressCity'
    | 'billingAddressState'
    | 'billingAddressPostalCode'
    | 'billingAddressCountry';
  apiKey:
    | 'company'
    | 'phone'
    | 'billing_address_line_1'
    | 'billing_address_line_2'
    | 'billing_address_city'
    | 'billing_address_state'
    | 'billing_address_postal_code'
    | 'billing_address_country';
  labelKey: string;
  placeholderKey: string;
  autoComplete?: string;
  maxLength?: number;
};

const CHECKOUT_FIELD_CONFIGS: CheckoutFieldConfig[] = [
  {
    key: 'company',
    formKey: 'company',
    apiKey: 'company',
    labelKey: 'checkout.fields.company',
    placeholderKey: 'checkout.placeholders.company',
    autoComplete: 'organization',
    maxLength: 255,
  },
  {
    key: 'contact_phone',
    formKey: 'phone',
    apiKey: 'phone',
    labelKey: 'checkout.fields.contact_phone',
    placeholderKey: 'checkout.placeholders.contact_phone',
    autoComplete: 'tel',
  },
  {
    key: 'billing.address_line_1',
    formKey: 'billingAddressLine1',
    apiKey: 'billing_address_line_1',
    labelKey: 'checkout.fields.billing.address_line_1',
    placeholderKey: 'checkout.placeholders.billing.address_line_1',
    autoComplete: 'address-line1',
  },
  {
    key: 'billing.address_line_2',
    formKey: 'billingAddressLine2',
    apiKey: 'billing_address_line_2',
    labelKey: 'checkout.fields.billing.address_line_2',
    placeholderKey: 'checkout.placeholders.billing.address_line_2',
    autoComplete: 'address-line2',
  },
  {
    key: 'billing.address_city',
    formKey: 'billingAddressCity',
    apiKey: 'billing_address_city',
    labelKey: 'checkout.fields.billing.address_city',
    placeholderKey: 'checkout.placeholders.billing.address_city',
    autoComplete: 'address-level2',
  },
  {
    key: 'billing.address_state',
    formKey: 'billingAddressState',
    apiKey: 'billing_address_state',
    labelKey: 'checkout.fields.billing.address_state',
    placeholderKey: 'checkout.placeholders.billing.address_state',
    autoComplete: 'address-level1',
  },
  {
    key: 'billing.address_postal_code',
    formKey: 'billingAddressPostalCode',
    apiKey: 'billing_address_postal_code',
    labelKey: 'checkout.fields.billing.address_postal_code',
    placeholderKey: 'checkout.placeholders.billing.address_postal_code',
    autoComplete: 'postal-code',
  },
  {
    key: 'billing.address_country',
    formKey: 'billingAddressCountry',
    apiKey: 'billing_address_country',
    labelKey: 'checkout.fields.billing.address_country',
    placeholderKey: 'checkout.placeholders.billing.address_country',
    autoComplete: 'country',
  },
];

const normalizeOptional = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
};

const resolveCheckoutFieldConfig = (key: PublicCheckoutFieldKey) =>
  CHECKOUT_FIELD_CONFIGS.find((field) => field.key === key);

const CHECKOUT_FIELD_API_TO_FORM_KEY = CHECKOUT_FIELD_CONFIGS.reduce<
  Partial<Record<CheckoutFieldConfig['apiKey'], CheckoutFieldConfig['formKey']>>
>((acc, config) => {
  acc[config.apiKey] = config.formKey;
  return acc;
}, {});

const isBillingField = (key: PublicCheckoutFieldKey) => key.startsWith('billing.');

const CHECKOUT_FIELD_WIDE = new Set<PublicCheckoutFieldKey>([
  'billing.address_line_1',
  'billing.address_line_2',
]);
const PHONE_PATTERN = /^\+?[0-9\s().-]{7,}$/;
const STRIPE_PUBLIC_KEY =
  typeof import.meta.env.VITE_STRIPE_PUBLIC_KEY === 'string'
    ? import.meta.env.VITE_STRIPE_PUBLIC_KEY
    : '';

const STRIPE_LOCALES = new Set<StripeElementLocale>([
  'auto',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'en',
  'en-GB',
  'es',
  'et',
  'fi',
  'fr',
  'hr',
  'hu',
  'id',
  'it',
  'ja',
  'ko',
  'lt',
  'lv',
  'mt',
  'ms',
  'nl',
  'no',
  'pl',
  'pt',
  'pt-BR',
  'ro',
  'ru',
  'sk',
  'sl',
  'sv',
  'th',
  'tr',
  'vi',
  'zh',
  'zh-HK',
  'zh-TW',
]);

const resolveStripeLocale = (language: string): StripeElementLocale => {
  const normalized = language.trim().replace('_', '-').toLowerCase();
  if (!normalized) {
    return 'auto';
  }

  const regionMap: Record<string, string> = {
    'en-gb': 'en-GB',
    'pt-br': 'pt-BR',
    'zh-hk': 'zh-HK',
    'zh-tw': 'zh-TW',
  };

  const mapped = regionMap[normalized] as StripeElementLocale | undefined;
  if (mapped && STRIPE_LOCALES.has(mapped)) {
    return mapped;
  }

  const base = normalized.split('-')[0];
  return STRIPE_LOCALES.has(base as StripeElementLocale)
    ? (base as StripeElementLocale)
    : 'auto';
};

function createCheckoutFormSchema(
  requireTerms: boolean,
  requiredFields: Set<PublicCheckoutFieldKey>,
  t: TFunction,
) {
  return z
    .object({
      firstName: z.string().min(1, { message: t('customers.register.validation.firstNameRequired') }),
      lastName: z.string().min(1, { message: t('customers.register.validation.lastNameRequired') }),
      email: z.string().email({ message: t('customers.register.validation.emailInvalid') }),
      company: z.string().max(255, { message: t('checkout.companyTooLong', 'Company name must be 255 characters or less.') }).optional(),
      phone: z.string().optional(),
      billingAddressLine1: z.string().optional(),
      billingAddressLine2: z.string().optional(),
      billingAddressCity: z.string().optional(),
      billingAddressState: z.string().optional(),
      billingAddressPostalCode: z.string().optional(),
      billingAddressCountry: z.string().optional(),
      role: z.enum(['individual', 'team_owner']).default('individual'),
      teamName: z.string().optional(),
      termsAccepted: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
      if (data.role === 'team_owner' && (!data.teamName || !data.teamName.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('checkout.workspaceNameRequired', 'Workspace name is required.'),
          path: ['teamName'],
        });
      }
      if (requireTerms && !data.termsAccepted) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('customers.register.termsRequired'),
          path: ['termsAccepted'],
        });
      }
      const requiredMessage = t('checkout.requiredField', 'This field is required.');
      const requiredMap: Record<
        PublicCheckoutFieldKey,
        | 'phone'
        | 'company'
        | 'billingAddressLine1'
        | 'billingAddressLine2'
        | 'billingAddressCity'
        | 'billingAddressState'
        | 'billingAddressPostalCode'
        | 'billingAddressCountry'
      > = {
        company: 'company',
        contact_phone: 'phone',
        'billing.address_line_1': 'billingAddressLine1',
        'billing.address_line_2': 'billingAddressLine2',
        'billing.address_city': 'billingAddressCity',
        'billing.address_state': 'billingAddressState',
        'billing.address_postal_code': 'billingAddressPostalCode',
        'billing.address_country': 'billingAddressCountry',
      };

      requiredFields.forEach((key) => {
        const fieldKey = requiredMap[key];
        const value = data[fieldKey];
        if (typeof value !== 'string' || value.trim().length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: requiredMessage,
            path: [fieldKey],
          });
        }
      });

      const phoneValue = data.phone?.trim() ?? '';
      if (phoneValue.length > 0 && !PHONE_PATTERN.test(phoneValue)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('checkout.phoneInvalid', 'Enter a valid phone number.'),
          path: ['phone'],
        });
      }
    });
}

type CheckoutFormData = z.infer<ReturnType<typeof createCheckoutFormSchema>>;

const buildPrefillValues = buildPrefillValuesUtil;

type Step = 'customer' | 'payment';
type PlanType = 'individual' | 'teams';
type CheckoutMode = 'plan' | 'package';
type CheckoutSupportDetails = {
  stripePaymentIntentId?: string | null;
  stripeSubscriptionId?: string | null;
  stripeSetupIntentId?: string | null;
};
type CheckoutSummaryItem = {
  label: string;
  value: string;
};
type StripeIntentType = 'payment' | 'setup';
type BillingDetails = {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
};
type PackagePricingOption = {
  currency: string;
  amount: number;
  priceId?: string;
};

const normalizePlanType = (type?: PublicPlan['type']): PlanType => (type === 'teams' ? 'teams' : 'individual');
const normalizePackageType = (pkg?: PublicCreditPackage): PlanType => {
  const raw = pkg?.type ?? pkg?.planType ?? pkg?.plan_type;
  return raw === 'teams' ? 'teams' : 'individual';
};
const resolveIncludedSeats = (plan?: PublicPlan | null): number | null => {
  if (!plan) return null;
  const record = plan as PublicPlan & { included_seats?: number | null };
  const value = plan.includedSeats ?? record.included_seats ?? null;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
};

const resolvePackagePricingOptions = (pkg: PublicCreditPackage): PackagePricingOption[] => {
  const options: PackagePricingOption[] = [];
  const priceIds =
    pkg.priceIds ??
    (pkg as PublicCreditPackage & { price_ids?: Record<string, string> }).price_ids ??
    {};
  const prices = Array.isArray(pkg.prices) ? {} : (pkg.prices ?? {});

  Object.entries(prices).forEach(([currency, amount]) => {
    if (typeof amount === 'number') {
      options.push({
        currency,
        amount,
        priceId: priceIds[currency],
      });
    }
  });

  const rawPricesArray: Array<Record<string, unknown>> = Array.isArray(pkg.pricesArray)
    ? pkg.pricesArray as Array<Record<string, unknown>>
    : Array.isArray((pkg as PublicCreditPackage & { prices_array?: unknown }).prices_array)
      ? (pkg as PublicCreditPackage & { prices_array?: unknown }).prices_array as Array<Record<string, unknown>>
      : Array.isArray(pkg.prices)
        ? pkg.prices as Array<Record<string, unknown>>
        : [];
  if (!rawPricesArray.length) {
    return options;
  }

  const readString = (value: unknown) => (typeof value === 'string' ? value : undefined);
  const readNumber = (value: unknown) => (typeof value === 'number' ? value : undefined);

  rawPricesArray.forEach((entry) => {
    const record = entry as Record<string, unknown>;
    const currency =
      readString(record.currency) ??
      readString((record.attributes as Record<string, unknown> | undefined)?.currency);
    const amount =
      readNumber(record.amount) ??
      readNumber(record.basePrice) ??
      readNumber(record.base_price) ??
      readNumber((record.attributes as Record<string, unknown> | undefined)?.amount) ??
      readNumber((record.attributes as Record<string, unknown> | undefined)?.base_price);
    const priceId =
      readString(record.id) ??
      readString(record.priceId) ??
      readString(record.price_id);

    if (!currency) return;

    const existing = options.find((option) => option.currency === currency);
    if (existing) {
      if (priceId && !existing.priceId) {
        existing.priceId = priceId;
      }
      return;
    }

    if (typeof amount === 'number') {
      options.push({
        currency,
        amount,
        priceId,
      });
    }
  });

  return options;
};

const resolveBillingCycleLabel = (pricing: PublicPlanPricing, t: TFunction): string => {
  const labelFromId = t(`checkout.billingCycles.${pricing.billingCycleId}`, { defaultValue: '' });
  if (labelFromId) {
    return labelFromId;
  }

  const name = pricing.billingCycleName?.trim();
  const normalizedName = name?.toLowerCase();
  if (normalizedName === 'monthly billing') {
    return t('checkout.billingCycles.monthlyBilling', 'Monthly billing');
  }
  if (normalizedName === 'yearly billing') {
    return t('checkout.billingCycles.yearlyBilling', 'Yearly billing');
  }

  if (pricing.billingCycleMonths === 1) {
    return t('checkout.billingCycles.monthly', 'Monthly');
  }
  if (pricing.billingCycleMonths === 12) {
    return t('checkout.billingCycles.yearly', 'Yearly');
  }

  return name ?? pricing.billingCycleId ?? '--';
};

const formatCurrencyAmount = (amount: number, currency: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

const resolveRecurringCycleLabel = (pricing: PublicPlanPricing, t: TFunction): string => {
  if (pricing.billingCycleMonths === 1) {
    return t('checkout.cycleSuffixMonthly', 'per month');
  }
  if (pricing.billingCycleMonths === 12) {
    return t('checkout.cycleSuffixYearly', 'per year');
  }
  if (pricing.billingCycleMonths) {
    return t('checkout.cycleSuffixEveryMonths', {
      count: pricing.billingCycleMonths,
      defaultValue: 'per {{count}} months',
    });
  }
  return t('checkout.cycleSuffixMonthly', 'per month');
};

const resolveIntroSummary = (pricing: PublicPlanPricing, t: TFunction): string | null => {
  const introPrice = pricing.introPrice ?? null;
  const introCycles = pricing.introCycles ?? 0;
  const recurringPrice = pricing.recurringPrice ?? pricing.price;
  if (!introPrice || introPrice <= 0 || introCycles <= 0) return null;
  return t('checkout.introSummary', {
    introPrice: formatCurrencyAmount(introPrice, pricing.currency),
    cycles: introCycles,
    recurringPrice: formatCurrencyAmount(recurringPrice, pricing.currency),
    cycleSuffix: resolveRecurringCycleLabel(pricing, t),
  });
};

function createSimpleRegisterSchema(requireTerms: boolean, t: TFunction) {
  return z
    .object({
      firstName: z.string().min(1, { message: t('customers.register.validation.firstNameRequired') }),
      lastName: z.string().min(1, { message: t('customers.register.validation.lastNameRequired') }),
      email: z.string().email({ message: t('customers.register.validation.emailInvalid') }),
      company: z.string().optional(),
      phone: z.string().optional(),
      role: z.enum(['individual', 'team_owner']),
      teamName: z.string().optional(),
      termsAccepted: z.boolean().default(false),
    })
    .superRefine((data, ctx) => {
      if (data.role === 'team_owner' && (!data.teamName || !data.teamName.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('checkout.workspaceNameRequired', 'Workspace name is required.'),
          path: ['teamName'],
        });
      }
      if (requireTerms && !data.termsAccepted) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('customers.register.termsRequired'),
          path: ['termsAccepted'],
        });
      }
    });
}

type SimpleRegisterData = z.infer<ReturnType<typeof createSimpleRegisterSchema>>;

type StripePaymentFormProps = {
  clientSecret: string;
  intentType: StripeIntentType;
  billingDetails: BillingDetails;
  onSuccess: () => void;
  onError: (message: string) => void;
  onProcessingChange?: (value: boolean) => void;
  submitRef?: MutableRefObject<(() => void) | null>;
};

function StripePaymentForm({
  clientSecret,
  intentType,
  billingDetails,
  onSuccess,
  onError,
  onProcessingChange,
  submitRef,
}: StripePaymentFormProps) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const handleSubmit = async (event?: FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    if (!stripe || !elements) {
      onError(t('checkout.paymentUnavailable'));
      return;
    }

    onProcessingChange?.(true);
    const submitResult = await elements.submit();
    if (submitResult.error) {
      onProcessingChange?.(false);
      onError(submitResult.error.message || t('checkout.paymentError'));
      return;
    }
    const confirmParams = {
      payment_method_data: {
        billing_details: billingDetails,
      },
    };

    const result =
      intentType === 'setup'
        ? await stripe.confirmSetup({
            elements,
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          })
        : await stripe.confirmPayment({
            elements,
            clientSecret,
            confirmParams,
            redirect: 'if_required',
          });
    onProcessingChange?.(false);

    if (result.error) {
      onError(result.error.message || t('checkout.paymentError'));
      return;
    }

    onSuccess();
  };

  useEffect(() => {
    if (!submitRef) {
      return;
    }
    submitRef.current = () => {
      void handleSubmit();
    };
    return () => {
      submitRef.current = null;
    };
  }, [submitRef, stripe, elements, clientSecret, intentType, billingDetails]);

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      <PaymentElement />
    </form>
  );
}

function ProviderBrandMark({ size = 'lg' }: { size?: 'sm' | 'lg' }) {
  const provider = useProvider();
  const { t } = useTranslation();
  const providerName = provider?.name ?? t('customers.customer', 'Customer');
  const logo = useBrandLogo();
  const dimension = size === 'lg' ? 200 : 160;

  if (logo) {
    return (
      <img
        src={logo}
        alt={`${providerName} logo`}
        className="object-contain"
        style={{ maxWidth: dimension, maxHeight: 44 }}
      />
    );
  }

  return (
    <span className={cn('font-semibold tracking-tight', size === 'lg' ? 'text-2xl' : 'text-xl')}>
      {providerName}
    </span>
  );
}

function resolveInitialSelection(
  plans: PublicPlan[],
  planId?: string | null,
  cycleId?: string | null,
  defaults?: { plan?: string | null; cycle?: string | null },
): { plan: PublicPlan | null; pricing: PublicPlanPricing | null } {
  if (!plans.length) {
    return { plan: null, pricing: null };
  }

  const findPlan = (id?: string | null) => plans.find((plan) => plan.id === id) ?? null;
  const preferredPlan = findPlan(planId) ?? findPlan(defaults?.plan) ?? findPlan(defaults?.plan ?? defaults?.cycle) ?? plans[0];

  if (!preferredPlan) {
    return { plan: null, pricing: null };
  }

  const findPricing = (plan: PublicPlan) => {
    if (plan.pricings.length === 0) return null;
    if (cycleId) {
      const match = plan.pricings.find((price) => price.billingCycleId === cycleId && price.selectable !== false);
      if (match) return match;
    }
    if (defaults?.cycle) {
      const match = plan.pricings.find((price) => price.billingCycleId === defaults.cycle && price.selectable !== false);
      if (match) return match;
    }
    return plan.pricings.find((price) => price.selectable !== false) ?? plan.pricings[0];
  };

  return { plan: preferredPlan, pricing: findPricing(preferredPlan) };
}

const simpleDefaultValues: SimpleRegisterData = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  phone: '',
  role: 'individual',
  teamName: '',
  termsAccepted: false,
};

function SimpleRegister() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const termsUrlQuery = usePublicProviderTermsUrl();
  const termsUrl = termsUrlQuery.data ?? null;
  const termsUrlLoading = termsUrlQuery.isLoading || termsUrlQuery.isFetching;
  const simpleRegisterSchema = useMemo(
    () => createSimpleRegisterSchema(Boolean(termsUrl), t),
    [termsUrl, t],
  );
  const queryClient = useQueryClient();
  const createCustomer = useCreateProviderCustomer();
  const provisionTeam = useMutation({
    mutationFn: async (payload: { customerId: string; teamName: string }) => {
      return customersService.createCustomerTeam(payload.customerId, {
        name: payload.teamName,
        total_seats: 5,
        role: 'owner',
      });
    },
  });

  const [result, setResult] = useState<{ customer: ProviderCustomer; team?: CustomerTeam } | null>(null);
  const language = normalizeLanguageCode(i18n.language);

  const form = useForm<SimpleRegisterData>({
    resolver: zodResolver(simpleRegisterSchema),
    defaultValues: simpleDefaultValues,
  });

  const watchRole = form.watch('role');
  const isSubmitting = form.formState.isSubmitting || createCustomer.isPending || provisionTeam.isPending;

  const handleCreateAnother = () => {
    setResult(null);
    form.reset(simpleDefaultValues);
  };

  const handleCustomerLogin = () => {
    const authOrigin = resolveAuthOriginFromCheckout();
    if (authOrigin) {
      window.location.assign(new URL('/customer/login', authOrigin).toString());
      return;
    }
    navigate('/customer/login');
  };

  const summaryItems = useMemo(() => {
    if (!result) {
      return [];
    }

    return [
      {
        label: t('customers.register.summary.customerName'),
        value: result.customer.name,
      },
      {
        label: t('customers.register.summary.customerEmail'),
        value: result.customer.email,
      },
      result.customer.companyName
        ? {
            label: t('customers.register.summary.company'),
            value: result.customer.companyName,
          }
        : null,
      result.team
        ? {
            label: t('customers.register.summary.teamName'),
            value: `${result.team.name} (${t('customers.register.summary.seats', {
              used: result.team.members,
              total: result.team.total_seats,
            })})`,
          }
        : null,
    ].filter(Boolean) as Array<{ label: string; value: string }>;
  }, [result, t]);

  const onSubmit = async (data: SimpleRegisterData) => {
    if (termsUrlLoading) return;

    try {
      setResult(null);
      const normalizedEmail = normalizeEmail(data.email);
      const { customer, signupRedirectUrl } = await createCustomer.mutateAsync({
        firstName: data.firstName,
        lastName: data.lastName,
        email: normalizedEmail,
        language,
        company: data.company,
        phone: data.phone,
        role: data.role,
        teamName: data.teamName,
        sendInvite: true,
      });

      if (signupRedirectUrl) {
        toast.success(t('customers.register.successToast'));
        window.location.assign(signupRedirectUrl);
        return;
      }

      let team: CustomerTeam | undefined;

      if (data.role === 'team_owner' && data.teamName) {
        try {
          team = await provisionTeam.mutateAsync({
            customerId: customer.id,
            teamName: data.teamName,
          });
          queryClient.invalidateQueries({ queryKey: providerCustomerKeys.all });
        } catch (teamError) {
          const teamMessage =
            teamError instanceof Error ? teamError.message : t('customers.register.teamErrorToast');
          toast.error(teamMessage);
        }
      }

      setResult({ customer, team });
      toast.success(t('customers.register.successToast'));
      form.reset(simpleDefaultValues);
    } catch (error) {
      const message = error instanceof Error ? error.message : t('customers.register.errorToast');
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center">
          <ProviderBrandMark size="lg" />
        </div>

        {!result ? (
          <Card className="w-full">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">{t('customers.register.title')}</CardTitle>
              <CardDescription>{t('customers.register.subtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Form {...form}>
                <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.create.fields.firstName')}</FormLabel>
                          <FormControl>
                            <Input {...field} autoComplete="given-name" disabled={isSubmitting} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('customers.create.fields.lastName')}</FormLabel>
                          <FormControl>
                            <Input {...field} autoComplete="family-name" disabled={isSubmitting} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.create.fields.email')}</FormLabel>
                        <FormControl>
                          <Input {...field} type="email" autoComplete="email" disabled={isSubmitting} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('customers.create.fields.accountType')}</FormLabel>
                        <FormControl>
                          <div className="grid gap-3 sm:grid-cols-2">
                            <button
                              type="button"
                              onClick={() => field.onChange('individual')}
                              className={cn(
                                'rounded-lg border p-4 text-left transition',
                                field.value === 'individual' ? 'border-primary bg-primary/5' : 'border-muted',
                              )}
                              disabled={isSubmitting}
                            >
                              <div className="flex items-center justify-between">
                                <span>{t('customers.create.fields.individual')}</span>
                                <Badge variant="outline">{t('checkout.default')}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {t('customers.register.individualCopy')}
                              </p>
                            </button>
                            <button
                              type="button"
                              onClick={() => field.onChange('team_owner')}
                              className={cn(
                                'rounded-lg border p-4 text-left transition',
                                field.value === 'team_owner' ? 'border-primary bg-primary/5' : 'border-muted',
                              )}
                              disabled={isSubmitting}
                            >
                              <div className="flex items-center justify-between">
                                <span>{t('customers.create.fields.teamOwner')}</span>
                                <Badge variant="outline">{t('checkout.teams')}</Badge>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {t('customers.register.teamOwnerCopy')}
                              </p>
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {watchRole === 'team_owner' && (
                    <div className="space-y-4 rounded-lg border p-4 bg-muted/50">
                      <div className="space-y-2">
                        <Label>{t('customers.register.teamSectionTitle')}</Label>
                        <p className="text-sm text-muted-foreground">
                          {t('customers.register.teamSectionDescription')}
                        </p>
                      </div>
                      <FormField
                        control={form.control}
                        name="teamName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('customers.create.fields.teamName')}</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                disabled={isSubmitting}
                                placeholder={t('customers.create.fields.teamNamePlaceholder')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {termsUrl ? (
                    <FormField
                      control={form.control}
                      name="termsAccepted"
                      render={({ field }) => (
                        <FormItem className="space-y-2">
                          <div className="flex items-start space-x-3 rounded-md border p-3">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={(checked) => field.onChange(checked === true)}
                                disabled={isSubmitting || termsUrlLoading}
                              />
                            </FormControl>
                            <div className="space-y-1 text-sm leading-tight">
                              <FormLabel className="text-sm font-medium leading-none">
                                {t('customers.register.termsLabelPrefix', 'I agree to the')}{' '}
                                <a
                                  href={termsUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-primary underline"
                                >
                                  {t('common.termsOfService')}
                                </a>
                                {t('customers.register.termsLabelSuffix', '')}
                              </FormLabel>
                            </div>
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ) : null}

                  <Button type="submit" className="w-full" disabled={isSubmitting || termsUrlLoading}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('customers.register.submitting')}
                      </>
                    ) : (
                      <>
                        <ArrowRight className="mr-2 h-4 w-4" />
                        {t('customers.register.submitCta')}
                      </>
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4">
              <div className="text-sm text-center text-muted-foreground">
                {t('customers.register.haveAccount')}{' '}
                <Button variant="link" className="px-0" onClick={handleCustomerLogin}>
                  {t('auth.customerSignIn')}
                </Button>
              </div>
              <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground">
                <a href="#" className="hover:text-primary">{t('common.privacyPolicy')}</a>
                {termsUrl ? (
                  <a
                    href={termsUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-primary"
                  >
                    {t('common.termsOfService')}
                  </a>
                ) : null}
                <a href="#" className="hover:text-primary">{t('common.helpSupport')}</a>
              </div>
            </CardFooter>
          </Card>
        ) : (
          <Card className="w-full">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl">{t('customers.register.successTitle')}</CardTitle>
              <CardDescription>{t('customers.register.successSubtitle')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                {summaryItems.map((item) => (
                  <div key={item.label} className="flex items-center justify-between rounded-lg border p-3">
                    <span className="text-sm text-muted-foreground">{item.label}</span>
                    <span className="text-sm font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
              {result.team ? (
                <div className="rounded-lg border p-4 bg-muted/50">
                  <p className="font-medium">{result.team.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('customers.register.teamSummary', {
                      members: result.team.members,
                      total: result.team.total_seats,
                    })}
                  </p>
                </div>
              ) : null}
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full" onClick={handleCreateAnother}>
                {t('customers.register.createAnotherCta')}
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
}

export default function CustomerCheckout() {
  useAuthPageTheme({ forcedTheme: 'light' });
  const { t, i18n } = useTranslation();

  const theme = useResolvedTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const productId = searchParams.get('productId');
  const planParam = searchParams.get('planId');
  const cycleParam = searchParams.get('cycleId');
  const priceParam = searchParams.get('priceId');
  const packageParam = searchParams.get('packageId');
  const currencyParam = searchParams.get('currency');
  const checkoutToken = searchParams.get('checkout_token');
  const planTypeParam = searchParams.get('planType');
  const resolvedPlanTypeParam = planTypeParam === 'teams' ? 'teams' : planTypeParam === 'individual' ? 'individual' : null;
  const language = normalizeLanguageCode(i18n.language);

  const [activeStep, setActiveStep] = useState<Step>('customer');
  const [checkoutMode, setCheckoutMode] = useState<CheckoutMode>('plan');
  const [planTypeFilter, setPlanTypeFilter] = useState<PlanType>(resolvedPlanTypeParam ?? 'individual');
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [selectedPackageCurrency, setSelectedPackageCurrency] = useState<string | null>(null);
  const [selectedPackagePriceId, setSelectedPackagePriceId] = useState<string | null>(null);
  const planTypeInitialized = useRef(false);
  const modeInitialized = useRef(false);
  const [pendingPayment, setPendingPayment] = useState<{
    clientSecret: string;
    intentType: StripeIntentType;
    billingDetails: BillingDetails;
    supportDetails: CheckoutSupportDetails;
  } | null>(null);
  const [pendingSummaryItems, setPendingSummaryItems] = useState<CheckoutSummaryItem[]>([]);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [existingCustomerError, setExistingCustomerError] = useState<string | null>(null);
  const [stripeSubmitting, setStripeSubmitting] = useState(false);
  const stripeSubmitRef = useRef<(() => void) | null>(null);
  const prefillAppliedRef = useRef(false);
  const isCheckoutTokenLocked = Boolean(checkoutToken);

  const { data, isLoading, isError, error } = usePublicCheckout(productId, Boolean(productId), {
    planId: planParam,
    cycleId: cycleParam,
    priceId: priceParam,
    checkoutToken,
    lang: language ?? null,
  });
  const prefill = data?.prefill;
  const prefillValues = useMemo(() => buildPrefillValues(prefill), [prefill]);
  const lockedPrefillFields = useMemo(
    () => ({
      firstName: isCheckoutTokenLocked && Boolean(prefill?.name),
      lastName: isCheckoutTokenLocked && Boolean(prefill?.name),
      email: isCheckoutTokenLocked && Boolean(prefill?.email),
      company: isCheckoutTokenLocked && Boolean(prefill?.company),
      teamName: isCheckoutTokenLocked && Boolean(prefill?.workspaceName),
      phone: isCheckoutTokenLocked && Boolean(prefill?.phone),
      billingAddressLine1: isCheckoutTokenLocked && Boolean(prefill?.billingAddressLine1),
      billingAddressLine2: isCheckoutTokenLocked && Boolean(prefill?.billingAddressLine2),
      billingAddressCity: isCheckoutTokenLocked && Boolean(prefill?.billingAddressCity),
      billingAddressState: isCheckoutTokenLocked && Boolean(prefill?.billingAddressState),
      billingAddressPostalCode: isCheckoutTokenLocked && Boolean(prefill?.billingAddressPostalCode),
      billingAddressCountry: isCheckoutTokenLocked && Boolean(prefill?.billingAddressCountry),
    }),
    [isCheckoutTokenLocked, prefill],
  );
  const submitCheckout = useSubmitPublicCheckout();
  const termsUrlQuery = usePublicProviderTermsUrl();
  const termsUrl = termsUrlQuery.data ?? null;
  const termsUrlLoading = termsUrlQuery.isLoading || termsUrlQuery.isFetching;
  const checkoutCopyQuery = usePublicProviderCheckoutContent();
  const checkoutCopy = checkoutCopyQuery.data;
  const checkoutFields = useMemo<PublicCheckoutFieldSetting[]>(() => data?.checkoutFields ?? [], [data?.checkoutFields]);
  const countryOptions = data?.countryOptions ?? null;
  const enabledCheckoutFields = useMemo(
    () =>
      checkoutFields
        .filter((field) => field.enabled)
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.key.localeCompare(b.key);
        }),
    [checkoutFields],
  );
  const enabledCheckoutFieldConfigs = useMemo(
    () =>
      enabledCheckoutFields
        .map((field) => ({
          field,
          config: resolveCheckoutFieldConfig(field.key),
        }))
        .filter(
          (
            item,
          ): item is {
            field: PublicCheckoutFieldSetting;
            config: CheckoutFieldConfig;
          } => Boolean(item.config),
        ),
    [enabledCheckoutFields],
  );
  const phoneFieldConfig = useMemo(
    () => enabledCheckoutFieldConfigs.find((item) => item.field.key === 'contact_phone'),
    [enabledCheckoutFieldConfigs],
  );
  const additionalCheckoutFields = useMemo(
    () => enabledCheckoutFieldConfigs.filter((item) => item.field.key !== 'contact_phone'),
    [enabledCheckoutFieldConfigs],
  );
  const requiredCheckoutFieldKeys = useMemo(
    () => enabledCheckoutFields.filter((field) => field.required).map((field) => field.key),
    [enabledCheckoutFields],
  );
  const requiredCheckoutFieldSet = useMemo(
    () => new Set(requiredCheckoutFieldKeys),
    [requiredCheckoutFieldKeys],
  );
  const checkoutFormSchema = useMemo(
    () => createCheckoutFormSchema(Boolean(termsUrl), requiredCheckoutFieldSet, t),
    [termsUrl, requiredCheckoutFieldSet, t],
  );

  const handleCustomerLogin = () => {
    const authOrigin = resolveAuthOriginFromCheckout();
    if (authOrigin) {
      window.location.assign(new URL('/customer/login', authOrigin).toString());
      return;
    }
    navigate('/customer/login');
  };

  if (!productId) {
    return <SimpleRegister />;
  }

  const allPlans = data?.plans ?? [];
  const purchasablePlans = useMemo(
    () => allPlans.filter((plan) => plan.pricings.length > 0),
    [allPlans],
  );
  const creditPackages = data?.creditPackages ?? [];
  const planTypeCounts = useMemo(
    () =>
      purchasablePlans.reduce(
        (acc, plan) => {
          const type = normalizePlanType(plan.type);
          acc[type] = (acc[type] ?? 0) + 1;
          return acc;
        },
        { individual: 0, teams: 0 } as Record<PlanType, number>,
      ),
    [purchasablePlans],
  );
  const packageTypeCounts = useMemo(
    () =>
      creditPackages.reduce(
        (acc, pkg) => {
          const type = normalizePackageType(pkg);
          acc[type] = (acc[type] ?? 0) + 1;
          return acc;
        },
        { individual: 0, teams: 0 } as Record<PlanType, number>,
      ),
    [creditPackages],
  );
  const filteredPlans = useMemo(
    () => purchasablePlans.filter((plan) => normalizePlanType(plan.type) === planTypeFilter),
    [planTypeFilter, purchasablePlans],
  );
  const visiblePlans = filteredPlans.length ? filteredPlans : purchasablePlans;
  const filteredPackages = useMemo(
    () => creditPackages.filter((pkg) => normalizePackageType(pkg) === planTypeFilter),
    [creditPackages, planTypeFilter],
  );
  const visiblePackages = filteredPackages.length ? filteredPackages : creditPackages;

  useEffect(() => {
    if (planTypeInitialized.current || !purchasablePlans.length) return;
    if (resolvedPlanTypeParam) {
      planTypeInitialized.current = true;
      setPlanTypeFilter(resolvedPlanTypeParam);
      return;
    }

    const paramPlan = purchasablePlans.find((plan) => plan.id === planParam);
    const recommendedPlan = purchasablePlans.find((plan) => plan.id === data?.recommendedPlanId);
    const defaultPlan =
      purchasablePlans.find((plan) => plan.id === data?.defaultPlanId) ??
      purchasablePlans[0];

    const nextType = normalizePlanType(paramPlan?.type ?? recommendedPlan?.type ?? defaultPlan?.type);
    setPlanTypeFilter(nextType);
    planTypeInitialized.current = true;
  }, [data?.defaultPlanId, data?.recommendedPlanId, planParam, purchasablePlans, resolvedPlanTypeParam]);

  useEffect(() => {
    if (modeInitialized.current || !data) return;
    if (packageParam) {
      setCheckoutMode('package');
      modeInitialized.current = true;
      return;
    }
    if (!purchasablePlans.length && creditPackages.length) {
      setCheckoutMode('package');
      modeInitialized.current = true;
      return;
    }
    modeInitialized.current = true;
  }, [creditPackages.length, data, packageParam, purchasablePlans.length]);

  useEffect(() => {
    if (!data || checkoutMode === 'package') return;

    const initialPlan = visiblePlans.find((plan: PublicPlan) => plan.id === planParam);
    const initialPricing =
      initialPlan?.pricings.find((pricing: PublicPlanPricing) =>
        priceParam ? pricing.id === priceParam : pricing.billingCycleId === cycleParam,
      ) ??
      null;

    if (initialPlan && initialPricing) {
      setSelectedPlanId(initialPlan.id);
      setSelectedCycleId(initialPricing.billingCycleId ?? null);
      setSelectedPriceId(initialPricing.id);
      return;
    }

    const { plan, pricing } = resolveInitialSelection(visiblePlans, planParam, cycleParam, {
      plan: data.recommendedPlanId ?? data.defaultPlanId ?? null,
      cycle: data.defaultBillingCycleId ?? null,
    });

    setSelectedPlanId(plan?.id ?? null);
    setSelectedCycleId(pricing?.billingCycleId ?? data.defaultBillingCycleId ?? null);
    setSelectedPriceId(pricing?.id ?? data.defaultPricingId ?? null);
  }, [checkoutMode, cycleParam, data, planParam, priceParam, visiblePlans]);

  useEffect(() => {
    if (!data || checkoutMode !== 'package' || !visiblePackages.length) return;

    const initialPackage = visiblePackages.find((pkg) => pkg.id === packageParam) ?? visiblePackages[0];
    const pricingOptions = resolvePackagePricingOptions(initialPackage);
    const fallbackCurrency =
      currencyParam ??
      pricingOptions.find((option) => option.currency === data.defaultCurrency)?.currency ??
      pricingOptions[0]?.currency ??
      data.defaultCurrency ??
      null;
    const matchedPrice = pricingOptions.find((option) => option.currency === fallbackCurrency);
    const fallbackPriceId = priceParam ?? matchedPrice?.priceId ?? null;

    setSelectedPackageId(initialPackage?.id ?? null);
    setSelectedPackageCurrency(fallbackCurrency);
    setSelectedPackagePriceId(fallbackPriceId);
  }, [checkoutMode, currencyParam, data, packageParam, priceParam, visiblePackages]);

  const form = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      company: '',
      phone: '',
      billingAddressLine1: '',
      billingAddressLine2: '',
      billingAddressCity: '',
      billingAddressState: '',
      billingAddressPostalCode: '',
      billingAddressCountry: '',
      role: planTypeFilter === 'teams' ? 'team_owner' : 'individual',
      teamName: '',
      termsAccepted: false,
    },
  });
  const watchRole = form.watch('role');
  const effectiveRole = planTypeFilter === 'teams' ? 'team_owner' : watchRole;

  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [checkoutToken, productId]);

  useEffect(() => {
    if (!prefill || prefillAppliedRef.current) {
      return;
    }
    const currentValues = form.getValues();
    form.reset({
      ...currentValues,
      ...prefillValues,
    });
    prefillAppliedRef.current = true;
  }, [form, prefill, prefillValues]);

  const selectedPlan = visiblePlans.find((plan) => plan.id === selectedPlanId) ?? null;
  const selectedPricing =
    selectedPlan?.pricings.find((pricing) => pricing.id === selectedPriceId) ??
    selectedPlan?.pricings.find((pricing) => pricing.billingCycleId === selectedCycleId) ??
    null;
  const selectedIntroSummary = selectedPricing ? resolveIntroSummary(selectedPricing, t) : null;
  const selectedPlanIncludedSeats = resolveIncludedSeats(selectedPlan);

  const selectedPackage = visiblePackages.find((pkg) => pkg.id === selectedPackageId) ?? null;
  const packagePricingOptions = selectedPackage ? resolvePackagePricingOptions(selectedPackage) : [];
  const resolvedPackagePricing =
    packagePricingOptions.find((option) => option.currency === selectedPackageCurrency) ??
    packagePricingOptions[0] ??
    null;

  const pricingAvailable = Boolean(selectedPlan && selectedPlan.pricings.length > 0);
  const packagePricingAvailable = packagePricingOptions.length > 0;

  const selectedPlanType = normalizePlanType(selectedPlan?.type);
  const isTeamPlanSelected = selectedPlanType === 'teams';
  const requiresTeamDetails =
    checkoutMode === 'plan' && (selectedPlanType === 'teams' || planTypeFilter === 'teams');
  const isTeamRole = effectiveRole === 'team_owner';
  const teamNameError = form.formState.errors.teamName;
  const hasPackages = creditPackages.length > 0;
  const hasPlans = purchasablePlans.length > 0;
  const showModeToggle = hasPackages && hasPlans;
  const selectedSummaryName =
    checkoutMode === 'package' ? selectedPackage?.name : selectedPlan?.name;
  const summaryLabel = checkoutMode === 'package' ? t('checkout.selectedPackage') : t('checkout.selectedPlan');
  const selectionLockedClass = isCheckoutTokenLocked ? 'pointer-events-none' : '';

  useEffect(() => {
    form.setValue('role', planTypeFilter === 'teams' ? 'team_owner' : 'individual');
  }, [form, planTypeFilter]);

  const triggerTeamNameValidationIfMissing = () => {
    if (!requiresTeamDetails || !isTeamRole) {
      return;
    }
    const workspaceName = form.getValues('teamName');
    if (workspaceName?.trim()) {
      return;
    }
    void form.trigger('teamName');
  };

  const handlePlanSelect = (planId: string, pricing?: PublicPlanPricing | null) => {
    if (isCheckoutTokenLocked) {
      return;
    }
    if (checkoutMode !== 'plan') {
      setCheckoutMode('plan');
      modeInitialized.current = true;
    }
    const plan = visiblePlans.find((p) => p.id === planId);
    if (!plan) return;
    const price = pricing ?? plan.pricings.find((p) => p.selectable !== false) ?? plan.pricings[0] ?? null;
    setSelectedPlanId(plan.id);
    setSelectedPriceId(price?.id ?? null);
    setSelectedCycleId(price?.billingCycleId ?? null);
    setSelectedPackageId(null);
    setSelectedPackageCurrency(null);
    setSelectedPackagePriceId(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('planId', plan.id);
    nextParams.delete('packageId');
    nextParams.delete('currency');
    if (price?.billingCycleId) {
      nextParams.set('cycleId', price.billingCycleId);
    } else {
      nextParams.delete('cycleId');
    }
    if (price?.id) {
      nextParams.set('priceId', price.id);
    } else {
      nextParams.delete('priceId');
    }
    setSearchParams(nextParams, { replace: true });

    const planType = normalizePlanType(plan.type);
    setPlanTypeFilter(planType);
    form.setValue('role', planType === 'teams' ? 'team_owner' : 'individual');
  };

  const handlePricingSelect = (pricing: PublicPlanPricing) => {
    if (isCheckoutTokenLocked) {
      return;
    }
    setSelectedCycleId(pricing.billingCycleId ?? null);
    setSelectedPriceId(pricing.id);

    const nextParams = new URLSearchParams(searchParams);
    if (pricing.billingCycleId) {
      nextParams.set('cycleId', pricing.billingCycleId);
    } else {
      nextParams.delete('cycleId');
    }
    if (pricing.id) {
      nextParams.set('priceId', pricing.id);
    } else {
      nextParams.delete('priceId');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handlePackageSelect = (packageId: string, pricing?: PackagePricingOption | null) => {
    if (isCheckoutTokenLocked) {
      return;
    }
    const pkg = visiblePackages.find((item) => item.id === packageId);
    if (!pkg) return;
    if (checkoutMode !== 'package') {
      setCheckoutMode('package');
      modeInitialized.current = true;
    }

    const pricingOptions = resolvePackagePricingOptions(pkg);
    const priceOption = pricing ?? pricingOptions[0] ?? null;

    setSelectedPackageId(pkg.id);
    setSelectedPackageCurrency(priceOption?.currency ?? null);
    setSelectedPackagePriceId(priceOption?.priceId ?? null);
    setSelectedPlanId(null);
    setSelectedCycleId(null);
    setSelectedPriceId(null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('packageId', pkg.id);
    nextParams.delete('planId');
    nextParams.delete('cycleId');
    if (priceOption?.currency) {
      nextParams.set('currency', priceOption.currency);
    } else {
      nextParams.delete('currency');
    }
    if (priceOption?.priceId) {
      nextParams.set('priceId', priceOption.priceId);
    } else {
      nextParams.delete('priceId');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handlePackagePricingSelect = (option: PackagePricingOption) => {
    if (isCheckoutTokenLocked) {
      return;
    }
    setSelectedPackageCurrency(option.currency);
    setSelectedPackagePriceId(option.priceId ?? null);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('currency', option.currency);
    if (option.priceId) {
      nextParams.set('priceId', option.priceId);
    } else {
      nextParams.delete('priceId');
    }
    setSearchParams(nextParams, { replace: true });
  };

  // handleModeToggle removed with mode toggle UI

  const goToPayment = form.handleSubmit(() => {
    if (termsUrlLoading) return;

    if (checkoutMode === 'plan') {
      if (!pricingAvailable || !selectedPricing) {
        toast.error(t('checkout.noPricing'));
        return;
      }
    } else {
      if (!packagePricingAvailable || !resolvedPackagePricing || !selectedPackage) {
        toast.error(t('checkout.noPackagePricing'));
        return;
      }
    }
    setActiveStep('payment');
  });

  const onSubmit = form.handleSubmit(async (values) => {
    if (termsUrlLoading) return;

    if (!data) {
      toast.error(t('checkout.selectionMissing'));
      return;
    }

    if (checkoutMode === 'plan') {
      if (!selectedPlan || !selectedPricing) {
        toast.error(t('checkout.selectionMissing'));
        return;
      }
    } else {
      if (!selectedPackage || !resolvedPackagePricing) {
        toast.error(t('checkout.selectionMissing'));
        return;
      }
    }

    setPendingPayment(null);
    setPaymentError(null);
    setExistingCustomerError(null);
    form.clearErrors(CHECKOUT_FIELD_CONFIGS.map((config) => config.formKey));

    try {
      const fullName = `${values.firstName} ${values.lastName}`.trim();
      const normalizedEmail = normalizeEmail(values.email);
      const billingAddress = {
        line1: normalizeOptional(values.billingAddressLine1),
        line2: normalizeOptional(values.billingAddressLine2),
        city: normalizeOptional(values.billingAddressCity),
        state: normalizeOptional(values.billingAddressState),
        postal_code: normalizeOptional(values.billingAddressPostalCode),
        country: normalizeOptional(values.billingAddressCountry),
      };
      const hasAddress = Object.values(billingAddress).some((value) => Boolean(value));
      const billingDetails: BillingDetails = {
        name: fullName || undefined,
        email: normalizeOptional(normalizedEmail),
        phone: normalizeOptional(values.phone),
        address: hasAddress ? billingAddress : undefined,
      };

      const checkoutFieldPayload = CHECKOUT_FIELD_CONFIGS.reduce<Record<string, string | undefined>>(
        (acc, config) => {
          const value = normalizeOptional(values[config.formKey]);
          if (value) {
            acc[config.apiKey] = value;
          }
          return acc;
        },
        {},
      );
      const response = await submitCheckout.mutateAsync(
        checkoutMode === 'package'
          ? {
              checkout_token: checkoutToken ?? undefined,
              product_id: data.productId,
              package_id: selectedPackage!.id,
              price_id: resolvedPackagePricing?.priceId ?? selectedPackagePriceId ?? undefined,
              currency: resolvedPackagePricing?.currency ?? selectedPackageCurrency ?? undefined,
              form: {
                name: fullName,
                email: normalizedEmail,
                ...(language ? { language } : {}),
                ...checkoutFieldPayload,
              },
              utm: undefined,
            }
          : {
              checkout_token: checkoutToken ?? undefined,
              product_id: data.productId,
              plan_id: selectedPlan!.id,
              cycle_id: selectedPricing!.billingCycleId,
              price_id: selectedPricing!.id,
              form: {
                name: fullName,
                email: normalizedEmail,
                ...(language ? { language } : {}),
                ...checkoutFieldPayload,
                role: planTypeFilter === 'teams' ? 'team_owner' : values.role,
                team_name: planTypeFilter === 'teams' || values.role === 'team_owner' ? values.teamName || undefined : undefined,
              },
              utm: undefined,
            },
      );

      const isPackageCheckout = response.purchasableType === 'credit_package' || checkoutMode === 'package';
      const summaryItems: CheckoutSummaryItem[] = [
        {
          label: isPackageCheckout ? t('checkout.packageSummary') : t('checkout.plan'),
          value: isPackageCheckout
            ? selectedPackage?.name ?? t('checkout.noPackage')
            : selectedPlan?.name ?? t('checkout.noPlan'),
        },
        {
          label: isPackageCheckout ? t('checkout.creditsLabel') : t('checkout.billing'),
          value: isPackageCheckout
            ? t('checkout.creditsValue', {
                credits: selectedPackage?.credits?.toLocaleString() ?? '--',
              })
            : selectedPricing
              ? resolveBillingCycleLabel(selectedPricing, t)
              : '--',
        },
      ];

      setPendingSummaryItems(summaryItems);
      const supportDetails: CheckoutSupportDetails = {
        stripePaymentIntentId: response.stripePaymentIntentId ?? null,
        stripeSubscriptionId: response.stripeSubscriptionId ?? null,
        stripeSetupIntentId: response.stripeSetupIntentId ?? null,
      };

      if (!response.paymentRequired) {
        toast.success(t('checkout.completed'));
        handleCheckoutSuccess(summaryItems, supportDetails, false);
        return;
      }

      if (!response.clientSecret) {
        const errorMessage = t('checkout.paymentError');
        setPaymentError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      const intentType: StripeIntentType =
        response.stripeIntentType === 'setup' ? 'setup' : 'payment';

      setPaymentError(null);
      setPendingPayment({
        clientSecret: response.clientSecret,
        intentType,
        billingDetails,
        supportDetails,
      });
      toast.success(t('checkout.paymentRequired'));
    } catch (err) {
      if (err instanceof PublicCheckoutRequestError && err.errors) {
        Object.entries(err.errors).forEach(([key, messages]) => {
          if (!Array.isArray(messages) || messages.length === 0) {
            return;
          }

          const normalizedKey = key.startsWith('errors.') ? key.slice('errors.'.length) : key;
          if (!normalizedKey.startsWith('form.')) {
            return;
          }

          const fieldApiKey = normalizedKey.slice('form.'.length) as CheckoutFieldConfig['apiKey'];
          const formField = CHECKOUT_FIELD_API_TO_FORM_KEY[fieldApiKey];
          if (!formField) {
            return;
          }

          form.setError(formField, {
            type: 'server',
            message: messages[0] ?? t('checkout.error'),
          });
        });
      }

      if (err instanceof Error && 'status' in err) {
        const status = (err as { status?: number }).status;
        const code = (err as { code?: string }).code;
        if (status === 409 || code === 'customer_exists') {
          setExistingCustomerError(t('checkout.alreadyRegistered'));
          return;
        }
      }
      const message =
        err instanceof Error && err.message
          ? err.message
          : t('checkout.error');
      toast.error(message);
    }
  });

  const marketingBullets = useMemo(
    () => [t('checkout.bullet1'), t('checkout.bullet2'), t('checkout.bullet3')],
    [t],
  );
  const heroTitle =
    (checkoutCopy?.headline && checkoutCopy.headline.trim()) || t('checkout.tagline');
  const heroSubheading =
    (checkoutCopy?.subheadline && checkoutCopy.subheadline.trim()) || t('checkout.subTagline');
  const heroBullets = useMemo(() => {
    const providerBullets =
      checkoutCopy?.bullets
        ?.map((item) => item?.trim())
        .filter((item): item is string => Boolean(item && item.length > 0)) ?? [];
    return providerBullets.length > 0 ? providerBullets : marketingBullets;
  }, [checkoutCopy?.bullets, marketingBullets]);
  const planTypeOptions: Array<{ value: PlanType; label: string; helper: string }> = [
    {
      value: 'individual',
      label: t('checkout.individualPlans'),
      helper: t('checkout.individualPlansHelper'),
    },
    {
      value: 'teams',
      label: t('checkout.teamPlans'),
      helper: t('checkout.teamPlansHelper'),
    },
  ];
  const availablePlanTypeOptions = planTypeOptions.filter((option) => planTypeCounts[option.value] > 0);
  const availablePackageTypeOptions = planTypeOptions.filter((option) => packageTypeCounts[option.value] > 0);
  const activePlanTypeOptions = checkoutMode === 'package' ? availablePackageTypeOptions : availablePlanTypeOptions;
  const activePlanTypeCounts = checkoutMode === 'package' ? packageTypeCounts : planTypeCounts;
  const stripePublicKey = (data?.stripePublishableKey ?? STRIPE_PUBLIC_KEY).trim();
  const stripeAccountId = data?.stripeAccountId ?? null;
  const stripePromise = useMemo(
    () =>
      stripePublicKey
        ? loadStripe(
            stripePublicKey,
            stripeAccountId ? { stripeAccount: stripeAccountId } : undefined,
          )
        : null,
    [stripeAccountId, stripePublicKey],
  );
  const stripeAvailable = Boolean(stripePromise);
  const stripeLocale = useMemo(() => resolveStripeLocale(i18n.language), [i18n.language]);
  const stripeAppearance = useMemo(
    () =>
      theme === 'dark'
        ? {
            theme: 'night' as const,
            variables: {
              colorPrimary: '#ffffff',
              colorBackground: '#0f0f0f',
              colorText: '#f1f5f9',
              colorTextSecondary: '#9ca3af',
              colorDanger: '#f87171',
              fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
            rules: {
              '.Input': {
                backgroundColor: '#0b0b0b',
                border: '1px solid #2a2a2a',
                boxShadow: 'none',
              },
              '.Input:focus': {
                border: '1px solid #4b5563',
                boxShadow: '0 0 0 1px #4b5563',
              },
              '.Label': {
                color: '#e5e7eb',
              },
              '.Tab': {
                backgroundColor: '#111111',
                border: '1px solid #2a2a2a',
              },
              '.Tab--selected': {
                backgroundColor: '#1f1f1f',
                borderColor: '#4b5563',
              },
              '.Block': {
                backgroundColor: '#0f0f0f',
                border: '1px solid #2a2a2a',
              },
            },
          }
        : {
            theme: 'stripe' as const,
            variables: {
              colorPrimary: '#111827',
              colorBackground: '#ffffff',
              colorText: '#111827',
              colorTextSecondary: '#6b7280',
              colorDanger: '#dc2626',
              fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, sans-serif',
              spacingUnit: '4px',
              borderRadius: '8px',
            },
            rules: {
              '.Input': {
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: 'none',
              },
              '.Input:focus': {
                border: '1px solid #9ca3af',
                boxShadow: '0 0 0 1px #9ca3af',
              },
              '.Label': {
                color: '#111827',
              },
              '.Tab': {
                backgroundColor: '#f9fafb',
                border: '1px solid #e5e7eb',
              },
              '.Tab--selected': {
                backgroundColor: '#ffffff',
                borderColor: '#9ca3af',
              },
              '.Block': {
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
              },
            },
          },
    [theme],
  );

  const isSubmitting = submitCheckout.isPending;

  const handleCheckoutSuccess = (
    summaryItems: CheckoutSummaryItem[],
    supportDetails: CheckoutSupportDetails,
    paymentRequired: boolean,
  ) => {
    const state = {
      summaryItems,
      paymentRequired,
      createdAt: Date.now(),
      stripePaymentIntentId: supportDetails.stripePaymentIntentId ?? null,
      stripeSubscriptionId: supportDetails.stripeSubscriptionId ?? null,
    };

    if (typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem('checkout.success', JSON.stringify(state));
      } catch {
        // Ignore storage errors (private mode, quota, etc.)
      }
    }

    redirectToSuccessPage('/customer/register/success');
  };

  const handleStripeSuccess = () => {
    if (!pendingPayment) {
      return;
    }
    handleCheckoutSuccess(pendingSummaryItems, pendingPayment.supportDetails, true);
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSwitcher />
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ProviderBrandMark size="sm" />
            <Badge variant="outline" className="text-xs">
              {t('checkout.secure')}
            </Badge>
          </div>
          {selectedSummaryName ? (
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{summaryLabel}</p>
              <p className="font-semibold">{selectedSummaryName}</p>
            </div>
          ) : null}
        </div>

        <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
          <Card className="hidden h-fit lg:block">
            <CardHeader className="space-y-2">
              <CardTitle className="text-2xl">{heroTitle}</CardTitle>
              <CardDescription>{heroSubheading}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {heroBullets.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-primary" />
                  <p className="text-sm text-muted-foreground">{item}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="shadow-lg">
            <CardHeader className="space-y-2">
              <CardTitle>{t('checkout.title')}</CardTitle>
              <CardDescription>
                {t('checkout.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {isLoading && (
                <div className="space-y-4">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              )}

              {isError && (
                <div className="flex items-center gap-3 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  <span>{error?.message ?? t('checkout.loadError')}</span>
                </div>
              )}

              {!isLoading && !isError && (
                <Tabs value={activeStep} onValueChange={(value) => setActiveStep(value as Step)}>
                  <TabsContent value="customer" className="space-y-6 pt-0">
                    <Form {...form}>
                      <form className="space-y-6" onSubmit={goToPayment}>
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              {checkoutMode === 'plan' && selectedPlan?.isRecommended && !showModeToggle ? (
                                <Badge variant="secondary" className="gap-1">
                                  <Sparkles className="h-3 w-3" />
                                  {t('checkout.recommended')}
                                </Badge>
                              ) : null}
                            </div>
                          </div>
                          {activePlanTypeOptions.length > 1 ? (
                            <div className="flex flex-wrap gap-2">
                              {activePlanTypeOptions.map((option) => (
                                <Button
                                  key={option.value}
                                  type="button"
                                  variant={planTypeFilter === option.value ? 'secondary' : 'outline'}
                                  size="sm"
                                  className="gap-2"
                                  onClick={() => {
                                    const nextType = option.value;
                                    setPlanTypeFilter(nextType);
                                    if (checkoutMode === 'plan') {
                                      form.setValue('role', nextType === 'teams' ? 'team_owner' : 'individual');
                                    }
                                    if (nextType !== planTypeFilter) {
                                      if (checkoutMode === 'plan') {
                                        setSelectedPlanId(null);
                                        setSelectedPriceId(null);
                                        setSelectedCycleId(null);
                                      } else {
                                        setSelectedPackageId(null);
                                        setSelectedPackageCurrency(null);
                                        setSelectedPackagePriceId(null);
                                      }
                                    }
                                  }}
                                >
                                  <span className="text-sm font-medium">{option.label}</span>
                                  <Badge variant={planTypeFilter === option.value ? 'default' : 'secondary'}>
                                    {activePlanTypeCounts[option.value]}
                                  </Badge>
                                  <span className="hidden text-xs text-muted-foreground sm:inline">{option.helper}</span>
                                </Button>
                              ))}
                            </div>
                          ) : null}
                          <div className="space-y-3">
                            {checkoutMode === 'plan' ? (
                              !visiblePlans.length ? (
                                <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                  <p>{t('checkout.noPlansForType')}</p>
                                  {activePlanTypeOptions.length > 1 ? (
                                    <p className="mt-1">
                                      {t('checkout.switchPlanType')}
                                    </p>
                                  ) : null}
                                </div>
                              ) : (
                                visiblePlans.map((plan: PublicPlan) => {
                                  const visiblePricings = priceParam
                                    ? plan.pricings.filter((pricing) => pricing.id === priceParam)
                                    : cycleParam
                                      ? plan.pricings.filter((pricing) => pricing.billingCycleId === cycleParam)
                                      : plan.pricings;
                                  const hasPricing = visiblePricings.length > 0;
                                  const planFeatures = plan.features ?? [];
                                  const visibleFeatures = planFeatures.slice(0, 4);
                                  const hasMoreFeatures = planFeatures.length > visibleFeatures.length;
                                  const includedSeats = resolveIncludedSeats(plan);
                                  const isTeamPlan = normalizePlanType(plan.type) === 'teams';
                                  const trialDays =
                                    typeof plan.trialDays === 'number' && plan.trialDays > 0
                                      ? plan.trialDays
                                      : null;
                                  const isTrialNoCardRequired =
                                    trialDays !== null && plan.trialRequiresCard === false;

                                  return (
                                  <button
                                    key={plan.id}
                                    type="button"
                                    onClick={() => handlePlanSelect(plan.id)}
                                    className={cn(
                                      'w-full rounded-xl border p-4 text-left transition hover:border-primary/50',
                                      selectedPlanId === plan.id ? 'border-primary bg-primary/5' : 'border-border',
                                      selectionLockedClass,
                                    )}
                                    aria-disabled={isCheckoutTokenLocked}
                                  >
                                      <div className="flex items-center justify-between gap-2">
                                        <div>
                                          <p className="font-semibold">{plan.name}</p>
                                          {plan.description ? (
                                            <p className="text-sm text-muted-foreground">{plan.description}</p>
                                          ) : null}
                                          {isTeamPlan && includedSeats !== null ? (
                                            <p className="mt-1 text-xs text-muted-foreground">
                                              {t('checkout.includedSeatsInline', { count: includedSeats })}
                                            </p>
                                          ) : null}
                                          {!hasPricing ? (
                                            <p className="text-xs text-amber-600">
                                              {t('checkout.noPricingForPlan')}
                                            </p>
                                          ) : null}
                                        </div>
                                        {plan.isRecommended || trialDays || isTrialNoCardRequired ? (
                                          <div className="flex flex-wrap items-center justify-end gap-2">
                                            {plan.isRecommended ? (
                                              <Badge variant="secondary" className="gap-1">
                                                <Wand2 className="h-3 w-3" />
                                                {t('checkout.recommended')}
                                              </Badge>
                                            ) : null}
                                            {trialDays ? (
                                              <Badge
                                                variant="outline"
                                                className="whitespace-nowrap border-emerald-500/40 text-emerald-500"
                                              >
                                                {t('checkout.freeTrialLabel', { count: trialDays })}
                                              </Badge>
                                            ) : null}
                                            {isTrialNoCardRequired ? (
                                              <Badge variant="outline" className="text-muted-foreground">
                                                {t('checkout.noCardRequired')}
                                              </Badge>
                                            ) : null}
                                          </div>
                                        ) : null}
                                      </div>
                                      {hasPricing ? (
                                        <div className="mt-3 flex flex-wrap gap-2">
                                          {visiblePricings.map((pricing: PublicPlanPricing) => {
                                            const introSummary = resolveIntroSummary(pricing, t);
                                            const displayAmount =
                                              introSummary && pricing.introPrice
                                                ? pricing.introPrice
                                                : pricing.price;

                                            return (
                                              <div
                                                key={pricing.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={(event) => {
                                                  event.stopPropagation();
                                                  if (pricing.selectable === false || isCheckoutTokenLocked) {
                                                    return;
                                                  }
                                                  handlePlanSelect(plan.id, pricing);
                                                  handlePricingSelect(pricing);
                                                }}
                                                onKeyDown={(event) => {
                                                  if (pricing.selectable === false || isCheckoutTokenLocked) {
                                                    return;
                                                  }
                                                  if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    handlePlanSelect(plan.id, pricing);
                                                    handlePricingSelect(pricing);
                                                  }
                                                }}
                                                className={cn(
                                                  'rounded-lg border px-3 py-2 text-sm transition cursor-pointer',
                                                  selectedPriceId === pricing.id
                                                    ? 'border-primary bg-primary/10'
                                                    : 'border-muted hover:border-primary/40',
                                                  pricing.selectable === false ? 'opacity-50 cursor-not-allowed' : '',
                                                  selectionLockedClass,
                                                )}
                                                aria-disabled={pricing.selectable === false || isCheckoutTokenLocked}
                                              >
                                                <div className="flex flex-col">
                                                  <span className="font-medium">
                                                    {pricing.currency} {displayAmount.toFixed(2)}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground">
                                                    {introSummary ?? resolveBillingCycleLabel(pricing, t)}
                                                  </span>
                                                </div>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      ) : null}
                                      {planFeatures.length > 0 ? (
                                        <div className="mt-3 space-y-1">
                                          <p className="text-xs font-medium text-muted-foreground">
                                            {t('checkout.features')}
                                          </p>
                                          <ul className="space-y-1 text-xs text-muted-foreground">
                                            {visibleFeatures.map((feature: string, index: number) => (
                                              <li key={`${plan.id}-${feature}-${index}`} className="flex items-start gap-2">
                                                <span className="text-primary">•</span>
                                                <span>{feature}</span>
                                              </li>
                                            ))}
                                            {hasMoreFeatures ? (
                                              <li className="text-xs text-muted-foreground">
                                                +{planFeatures.length - visibleFeatures.length}{' '}
                                                {t('checkout.moreFeatures')}
                                              </li>
                                            ) : null}
                                          </ul>
                                        </div>
                                      ) : null}
                                    </button>
                                  );
                                })
                              )
                            ) : !visiblePackages.length ? (
                              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                                <p>{t('checkout.noPackagesForType')}</p>
                              </div>
                            ) : (
                              visiblePackages.map((pkg: PublicCreditPackage) => {
                                const pricingOptions = resolvePackagePricingOptions(pkg);
                                const hasPricing = pricingOptions.length > 0;
                                return (
                                  <button
                                    key={pkg.id}
                                    type="button"
                                    onClick={() => handlePackageSelect(pkg.id)}
                                    className={cn(
                                      'w-full rounded-xl border p-4 text-left transition hover:border-primary/50',
                                      selectedPackageId === pkg.id ? 'border-primary bg-primary/5' : 'border-border',
                                      selectionLockedClass,
                                    )}
                                    aria-disabled={isCheckoutTokenLocked}
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <div>
                                        <p className="font-semibold">{pkg.name}</p>
                                        {pkg.description ? (
                                          <p className="text-sm text-muted-foreground">{pkg.description}</p>
                                        ) : null}
                                        <p className="mt-1 text-xs text-muted-foreground">
                                          {t('checkout.packageCredits', {
                                            credits: pkg.credits.toLocaleString(),
                                          })}
                                        </p>
                                        {!hasPricing ? (
                                          <p className="text-xs text-amber-600">
                                            {t('checkout.noPackagePricing')}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                    {hasPricing ? (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        {pricingOptions.map((option) => (
                                          <div
                                            key={`${pkg.id}-${option.currency}`}
                                            role="button"
                                            tabIndex={0}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              handlePackageSelect(pkg.id, option);
                                              handlePackagePricingSelect(option);
                                            }}
                                            onKeyDown={(event) => {
                                              if (event.key === 'Enter' || event.key === ' ') {
                                                event.preventDefault();
                                                handlePackageSelect(pkg.id, option);
                                                handlePackagePricingSelect(option);
                                              }
                                            }}
                                            className={cn(
                                              'rounded-lg border px-3 py-2 text-sm transition cursor-pointer',
                                              selectedPackageId === pkg.id && selectedPackageCurrency === option.currency
                                                ? 'border-primary bg-primary/10'
                                                : 'border-muted hover:border-primary/40',
                                              selectionLockedClass,
                                            )}
                                            aria-disabled={isCheckoutTokenLocked}
                                          >
                                            <span className="font-medium">
                                              {option.currency} {option.amount.toFixed(2)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : null}
                                  </button>
                                );
                              })
                            )}
                          </div>
                          {requiresTeamDetails ? (
                            <div className="space-y-3">
                              <div className="flex items-start justify-between gap-3">
                                <div className="space-y-1">
                                  <p className="text-sm font-medium">{t('checkout.teamHeading')}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {t('checkout.teamSubheading')}
                                  </p>
                                </div>
                                <Badge variant="outline" className="mt-0.5">
                                  {t('checkout.teamBadge')}
                                </Badge>
                              </div>
                              {!isTeamPlanSelected && (
                                <div className="flex flex-wrap gap-2">
                                  <Button
                                    type="button"
                                    variant={effectiveRole === 'individual' ? 'secondary' : 'outline'}
                                    size="sm"
                                    className="gap-2"
                                    onClick={() => {
                                      setPlanTypeFilter('individual');
                                      form.setValue('role', 'individual');
                                      setSelectedPlanId(null);
                                      setSelectedPriceId(null);
                                      setSelectedCycleId(null);
                                    }}
                                  >
                                    {t('checkout.roleIndividual')}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant={effectiveRole === 'team_owner' ? 'secondary' : 'outline'}
                                    size="sm"
                                    className="gap-2"
                                    onClick={() => form.setValue('role', 'team_owner')}
                                  >
                                    {t('checkout.roleTeamOwner')}
                                  </Button>
                                </div>
                              )}
                              {isTeamRole ? (
                                <FormField
                                  control={form.control}
                                  name="teamName"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>
                                        {t('checkout.teamName')}
                                        <span className="text-destructive"> *</span>
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          placeholder={t('checkout.teamNamePlaceholder')}
                                          className={teamNameError ? 'border-destructive focus-visible:ring-destructive/20' : undefined}
                                          onChange={(event) => {
                                            field.onChange(event);
                                            if (teamNameError) {
                                              void form.trigger('teamName');
                                            }
                                          }}
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <div className="space-y-3" onFocusCapture={triggerTeamNameValidationIfMissing}>
                          <p className="text-sm font-medium">{t('checkout.contact')}</p>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <FormField
                              control={form.control}
                              name="firstName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {t('customers.createDialog.fields.firstName')}
                                    <span className="text-destructive"> *</span>
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder={t('customers.createDialog.fields.firstName')}
                                      disabled={isSubmitting || lockedPrefillFields.firstName}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            <FormField
                              control={form.control}
                              name="lastName"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {t('customers.createDialog.fields.lastName')}
                                    <span className="text-destructive"> *</span>
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder={t('customers.createDialog.fields.lastName')}
                                      disabled={isSubmitting || lockedPrefillFields.lastName}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                          <div className="grid gap-4 sm:grid-cols-2">
                            <FormField
                              control={form.control}
                              name="email"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>
                                    {t('checkout.email')}
                                    <span className="text-destructive"> *</span>
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      type="email"
                                      placeholder={t('checkout.emailPlaceholder')}
                                      disabled={isSubmitting || lockedPrefillFields.email}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            {phoneFieldConfig ? (
                              <FormField
                                control={form.control}
                                name={phoneFieldConfig.config.formKey}
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      {t(phoneFieldConfig.config.labelKey)}
                                      {phoneFieldConfig.field.required ? (
                                        <span className="text-destructive"> *</span>
                                      ) : null}
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        autoComplete={phoneFieldConfig.config.autoComplete}
                                        placeholder={t(phoneFieldConfig.config.placeholderKey)}
                                        aria-required={phoneFieldConfig.field.required || undefined}
                                        disabled={isSubmitting || lockedPrefillFields.phone}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            ) : null}
                          </div>
                          {additionalCheckoutFields.length > 0 ? (
                            <div className="grid gap-4 sm:grid-cols-2">
                              {(() => {
                                let billingHeaderRendered = false;
                                return additionalCheckoutFields.flatMap(({ field: fieldSetting, config }) => {
                                  const nodes = [];
                                  if (isBillingField(fieldSetting.key) && !billingHeaderRendered) {
                                    billingHeaderRendered = true;
                                    nodes.push(
                                      <div
                                        key="billing-address-header"
                                        className="sm:col-span-2 pt-2 text-sm font-medium text-muted-foreground"
                                      >
                                        {t('checkout.billingAddressTitle')}
                                      </div>,
                                    );
                                  }
                                  nodes.push(
                                    <FormField
                                      key={fieldSetting.key}
                                      control={form.control}
                                      name={config.formKey}
                                      render={({ field }) => (
                                        <FormItem
                                          className={
                                            CHECKOUT_FIELD_WIDE.has(fieldSetting.key)
                                              ? 'sm:col-span-2'
                                              : undefined
                                          }
                                        >
                                          <FormLabel>
                                            {t(config.labelKey)}
                                            {fieldSetting.required ? (
                                              <span className="text-destructive"> *</span>
                                            ) : null}
                                          </FormLabel>
                                          <FormControl>
                                            {fieldSetting.key === 'billing.address_country' ? (
                                              <CountrySelect
                                                id={field.name}
                                                value={field.value ?? ''}
                                                placeholder={t(config.placeholderKey)}
                                                priorityOptions={countryOptions?.priority ?? []}
                                                remainingOptions={countryOptions?.remaining ?? []}
                                                onChange={(nextValue) => field.onChange(nextValue)}
                                                disabled={
                                                  isSubmitting ||
                                                  lockedPrefillFields[
                                                    config.formKey as keyof typeof lockedPrefillFields
                                                  ]
                                                }
                                                ariaInvalid={Boolean(form.formState.errors[config.formKey])}
                                              />
                                            ) : (
                                              <Input
                                                {...field}
                                                autoComplete={config.autoComplete}
                                                placeholder={t(config.placeholderKey)}
                                                aria-required={fieldSetting.required || undefined}
                                                maxLength={config.maxLength}
                                                disabled={
                                                  isSubmitting ||
                                                  lockedPrefillFields[
                                                    config.formKey as keyof typeof lockedPrefillFields
                                                  ]
                                                }
                                              />
                                            )}
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />,
                                  );
                                  return nodes;
                                });
                              })()}
                            </div>
                          ) : null}
                        </div>

                        {termsUrl ? (
                          <FormField
                            control={form.control}
                            name="termsAccepted"
                            render={({ field }) => (
                              <FormItem className="space-y-2">
                                <div className="flex items-start space-x-3 rounded-md border p-3">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked) => field.onChange(checked === true)}
                                  disabled={isSubmitting || termsUrlLoading}
                                />
                              </FormControl>
                              <div className="space-y-1 text-sm leading-tight">
                                <FormLabel className="text-sm font-medium leading-none">
                                  {t('checkout.termsLabelPrefix', 'I agree to the')}{' '}
                                  <a
                                    href={termsUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-primary underline"
                                  >
                                    {t('common.termsOfService')}
                                  </a>
                                  {t('checkout.termsLabelSuffix', '')}
                                </FormLabel>
                              </div>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                          />
                        ) : null}

                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={handleCustomerLogin}>
                            {t('checkout.haveAccount')}
                          </Button>
                          <Button
                            type="submit"
                            className="gap-2"
                            disabled={
                              (checkoutMode === 'plan'
                                ? (!selectedPlan ||
                                  !pricingAvailable ||
                                  (planTypeFilter === 'teams' && !isTeamRole))
                                : (!selectedPackage || !packagePricingAvailable)) ||
                              termsUrlLoading
                            }
                          >
                            {t('checkout.continue')}
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </TabsContent>

                  <TabsContent value="payment" className="space-y-6 pt-4">
                    <>
                      <div className="rounded-lg border p-4">
                        <div className="flex items-center justify-between">
                          <p className="font-medium">{t('checkout.review')}</p>
                          <Badge variant="outline">{t('checkout.stepLabel')}</Badge>
                        </div>
                        <div className="mt-3 space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {checkoutMode === 'package' ? t('checkout.packageSummary') : t('checkout.plan')}
                            </span>
                            <span className="font-medium">
                              {checkoutMode === 'package'
                                ? selectedPackage?.name ?? t('checkout.noPackage')
                                : selectedPlan?.name ?? t('checkout.noPlan')}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">
                              {checkoutMode === 'package' ? t('checkout.creditsLabel') : t('checkout.billing')}
                            </span>
                            <span className="font-medium">
                              {checkoutMode === 'package'
                                ? t('checkout.creditsValue', {
                                    credits: selectedPackage?.credits?.toLocaleString() ?? '--',
                                  })
                                : selectedPricing
                                  ? resolveBillingCycleLabel(selectedPricing, t)
                                  : '--'}
                            </span>
                          </div>
                          {checkoutMode !== 'package' && isTeamPlanSelected && selectedPlanIncludedSeats !== null ? (
                            <div className="flex items-center justify-between">
                              <span className="text-muted-foreground">{t('checkout.includedSeatsLabel')}</span>
                              <span className="font-medium">
                                {t('checkout.includedSeatsValue', { count: selectedPlanIncludedSeats })}
                              </span>
                            </div>
                          ) : null}
                          {checkoutMode !== 'package' && selectedIntroSummary ? (
                            <div className="flex items-center justify-end text-xs text-muted-foreground">
                              {selectedIntroSummary}
                            </div>
                          ) : null}
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground">{t('checkout.totalDue')}</span>
                            <span className="text-lg font-semibold">
                              {checkoutMode === 'package'
                                ? resolvedPackagePricing
                                  ? `${resolvedPackagePricing.currency} ${resolvedPackagePricing.amount.toFixed(2)}`
                                  : '--'
                                : selectedPricing
                                  ? `${selectedPricing.currency} ${(selectedIntroSummary && selectedPricing.introPrice
                                      ? selectedPricing.introPrice
                                      : selectedPricing.price
                                    ).toFixed(2)}`
                                  : '--'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {paymentError ? (
                        <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                          <AlertCircle className="h-4 w-4" />
                          <span>{paymentError}</span>
                        </div>
                      ) : null}

                      {existingCustomerError ? (
                        <div className="flex items-center justify-between gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
                          <span className="text-amber-200">{existingCustomerError}</span>
                          <Button variant="outline" size="sm" onClick={handleCustomerLogin}>
                            {t('checkout.goToLogin')}
                          </Button>
                        </div>
                      ) : null}

                      {pendingPayment?.clientSecret ? (
                        <div className="rounded-lg border p-4 space-y-4">
                          <p className="font-medium">{t('checkout.paymentDetails')}</p>
                          {pendingPayment.intentType === 'setup' ? (
                            <p className="text-sm text-muted-foreground">
                              {t('checkout.trialCardNotice')}
                            </p>
                          ) : null}
                          {stripeAvailable ? (
                            <Elements
                              key={`${pendingPayment.clientSecret}-${theme}-${stripeLocale}`}
                              stripe={stripePromise}
                              options={{
                                clientSecret: pendingPayment.clientSecret,
                                appearance: stripeAppearance,
                                locale: stripeLocale,
                              }}
                            >
                              <StripePaymentForm
                                clientSecret={pendingPayment.clientSecret}
                                intentType={pendingPayment.intentType}
                                billingDetails={pendingPayment.billingDetails}
                                onSuccess={handleStripeSuccess}
                                onError={(message) => setPaymentError(message)}
                                onProcessingChange={setStripeSubmitting}
                                submitRef={stripeSubmitRef}
                              />
                            </Elements>
                          ) : (
                            <p className="text-sm text-destructive">{t('checkout.paymentUnavailable')}</p>
                          )}
                        </div>
                      ) : null}

                      <div className="flex justify-between gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setActiveStep('customer');
                            setPendingPayment(null);
                            setPaymentError(null);
                            setExistingCustomerError(null);
                          }}
                        >
                          {t('checkout.back')}
                        </Button>
                        {pendingPayment ? (
                          <Button
                            onClick={() => stripeSubmitRef.current?.()}
                            disabled={isSubmitting || stripeSubmitting}
                            className="gap-2"
                          >
                            {(isSubmitting || stripeSubmitting) && (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            )}
                            {stripeSubmitting
                              ? t('checkout.processingPayment')
                              : t('checkout.payNow')}
                          </Button>
                        ) : (
                          <Button
                            onClick={onSubmit}
                            disabled={
                              isSubmitting ||
                              (checkoutMode === 'package'
                                ? !selectedPackage || !resolvedPackagePricing
                                : !selectedPlan || !selectedPricing) ||
                              termsUrlLoading
                            }
                            className="gap-2"
                          >
                            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            {t('checkout.complete')}
                          </Button>
                        )}
                      </div>
                    </>
                  </TabsContent>
                </Tabs>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
